import type { NodeExecution } from '@/types/monitoring';
import type { WorkflowDefinition, Action } from '@/types/workflowDefinition';

/**
 * Recursively collects all actions from a workflow definition tree,
 * flattening nested scope actions (If/Switch/Foreach/Until) into a single map.
 */
const flattenActions = (actions: Record<string, Action>): Record<string, Action> => {
  const result: Record<string, Action> = {};

  for (const [name, action] of Object.entries(actions)) {
    result[name] = action;

    // Use duck-typing to extract child actions from any scope type,
    // since the Zod union exhausts to `never` after discriminated checks.
    const raw = action as Record<string, unknown>;

    // Direct child actions (If true-branch, Foreach, Until, Scope)
    if (raw['actions'] && typeof raw['actions'] === 'object') {
      Object.assign(result, flattenActions(raw['actions'] as Record<string, Action>));
    }

    // If else-branch
    const elseBlock = raw['else'] as Record<string, unknown> | undefined;
    if (elseBlock?.['actions'] && typeof elseBlock['actions'] === 'object') {
      Object.assign(result, flattenActions(elseBlock['actions'] as Record<string, Action>));
    }

    // Switch cases
    const cases = raw['cases'] as Record<string, Record<string, unknown>> | undefined;
    if (cases && typeof cases === 'object') {
      for (const caseEntry of Object.values(cases)) {
        if (caseEntry['actions'] && typeof caseEntry['actions'] === 'object') {
          Object.assign(result, flattenActions(caseEntry['actions'] as Record<string, Action>));
        }
      }
    }

    // Switch default branch
    const defaultBlock = raw['default'] as Record<string, unknown> | undefined;
    if (defaultBlock?.['actions'] && typeof defaultBlock['actions'] === 'object') {
      Object.assign(result, flattenActions(defaultBlock['actions'] as Record<string, Action>));
    }
  }

  return result;
};

/**
 * Builds a child→parent map for nested workflow actions. For every action that
 * lives inside a scope (If, Switch, Foreach, Until, Scope), the map records the
 * name of the immediate scope parent. Top-level actions are not present.
 *
 * Used by the BottomPanel execution log to compute visual nesting depth for
 * children of non-iterating scopes (If/Switch/Scope). The runtime only emits
 * `parentNodeId` on Foreach/Until inner actions, so this map is the
 * authoritative source for the rest.
 */
export const buildScopeParentMap = (
  actions: Record<string, Action> | undefined,
): Map<string, string> => {
  const parentByChild = new Map<string, string>();
  if (!actions) return parentByChild;

  const walk = (children: Record<string, Action>, parentName?: string) => {
    for (const [name, action] of Object.entries(children)) {
      if (parentName) parentByChild.set(name, parentName);
      const raw = action as Record<string, unknown>;

      if (raw['actions'] && typeof raw['actions'] === 'object') {
        walk(raw['actions'] as Record<string, Action>, name);
      }
      const elseBlock = raw['else'] as Record<string, unknown> | undefined;
      if (elseBlock?.['actions'] && typeof elseBlock['actions'] === 'object') {
        walk(elseBlock['actions'] as Record<string, Action>, name);
      }
      const cases = raw['cases'] as Record<string, Record<string, unknown>> | undefined;
      if (cases && typeof cases === 'object') {
        for (const caseEntry of Object.values(cases)) {
          if (caseEntry['actions'] && typeof caseEntry['actions'] === 'object') {
            walk(caseEntry['actions'] as Record<string, Action>, name);
          }
        }
      }
      const defaultBlock = raw['default'] as Record<string, unknown> | undefined;
      if (defaultBlock?.['actions'] && typeof defaultBlock['actions'] === 'object') {
        walk(defaultBlock['actions'] as Record<string, Action>, name);
      }
    }
  };

  walk(actions);
  return parentByChild;
};

/**
 * Builds a topological sort order for actions based on their runAfter dependencies.
 * Recursively includes nested actions from scope types (If/Switch/Foreach/Until/Scope).
 * Actions with no dependencies (or dependencies on the trigger) come first.
 * Actions within parallel branches are ordered by their definition order (object key order).
 * 
 * @param actions - The workflow actions from the definition
 * @returns A map of action names to their position in the execution order
 */
export const buildActionOrderMap = (actions: Record<string, Action>): Map<string, number> => {
  const allActions = flattenActions(actions);
  const orderMap = new Map<string, number>();
  const actionNames = Object.keys(allActions);
  const actionNameSet = new Set(actionNames);
  
  if (actionNames.length === 0) {
    return orderMap;
  }

  // Build dependency graph
  const dependsOn = new Map<string, Set<string>>();
  
  actionNames.forEach((name) => {
    const action = allActions[name];
    const runAfter = action?.runAfter;
    
    if (runAfter && Object.keys(runAfter).length > 0) {
      dependsOn.set(name, new Set(Object.keys(runAfter)));
    } else {
      // No dependencies - runs after trigger
      dependsOn.set(name, new Set());
    }
  });

  // Topological sort with stable ordering for parallel branches
  const visited = new Set<string>();
  const inProgress = new Set<string>();
  let position = 0;

  const visit = (name: string): void => {
    if (visited.has(name)) return;
    if (inProgress.has(name)) {
      // Cycle detected - shouldn't happen in valid workflows, but handle gracefully
      console.warn(`[actionOrderingUtils] Cycle detected involving action: ${name}`);
      return;
    }

    inProgress.add(name);
    
    const deps = dependsOn.get(name) || new Set();
    
    // Visit dependencies first (in definition order to maintain stability)
    Array.from(deps)
      .filter((dep) => actionNameSet.has(dep))
      .forEach((dep) => visit(dep));
    
    inProgress.delete(name);
    visited.add(name);
    orderMap.set(name, position++);
  };

  // Process all actions in definition order (object key order)
  // This ensures that parallel branches maintain their relative order
  actionNames.forEach((name) => visit(name));

  return orderMap;
};

/**
 * Sorts node executions based on workflow definition order.
 * Falls back to start time sorting if workflow definition is not available.
 * 
 * Primary: workflow definition order (based on runAfter dependencies)
 * Secondary: for parallel branches, maintains definition order
 * Tertiary: start time (for actions not in definition or as fallback)
 * 
 * @param nodeExecutions - Array of node executions from the monitoring API
 * @param workflowDefinition - Optional workflow definition for proper ordering
 * @returns Sorted array of node executions
 */
export const sortNodeExecutions = (
  nodeExecutions: NodeExecution[],
  workflowDefinition?: WorkflowDefinition
): NodeExecution[] => {
  if (nodeExecutions.length === 0) {
    return [];
  }

  // If no workflow definition, fall back to start time
  if (!workflowDefinition?.definition?.actions) {
    return sortByStartTime(nodeExecutions);
  }

  const actionOrderMap = buildActionOrderMap(workflowDefinition.definition.actions);

  // Separate triggers and actions
  const triggers: NodeExecution[] = [];
  const actionsWithOrder: Array<{ exec: NodeExecution; order: number }> = [];
  const actionsWithoutOrder: NodeExecution[] = [];

  nodeExecutions.forEach((exec) => {
    if (exec.nodeId.startsWith('trigger-')) {
      triggers.push(exec);
    } else {
      // Extract action name from nodeId (format: "action-{actionName}")
      const actionName = exec.nodeId.replace(/^action-/, '');
      const order = actionOrderMap.get(actionName);
      
      if (order !== undefined) {
        actionsWithOrder.push({ exec, order });
      } else {
        actionsWithoutOrder.push(exec);
      }
    }
  });

  // Sort triggers by start time
  const sortedTriggers = sortByStartTime(triggers);

  // Sort actions by definition order
  actionsWithOrder.sort((a, b) => a.order - b.order);
  const sortedActions = actionsWithOrder.map((item) => item.exec);

  // Sort actions without order by start time
  const sortedActionsWithoutOrder = sortByStartTime(actionsWithoutOrder);

  // Combine: triggers first, then actions in definition order, then unknown actions
  return [...sortedTriggers, ...sortedActions, ...sortedActionsWithoutOrder];
};

/**
 * Helper function to sort executions by start time.
 * Used as fallback when definition order is not available.
 */
const sortByStartTime = (executions: NodeExecution[]): NodeExecution[] => {
  return [...executions].sort((a, b) => {
    if (!a.startTime) return 1;
    if (!b.startTime) return -1;
    return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
  });
};
